import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  ArrowLeft, Loader2, FileText, User, Mail, Search, RefreshCw, Shield, ChevronDown
} from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ACTION_LABELS = {
  'user.create':           { label: 'Created User',          color: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  'user.update':           { label: 'Updated User',          color: 'bg-blue-50 text-blue-700 border-blue-200' },
  'user.reset_password':   { label: 'Reset Password',        color: 'bg-amber-50 text-amber-700 border-amber-200' },
  'quote_request.update':  { label: 'Updated Quote Request', color: 'bg-purple-50 text-purple-700 border-purple-200' },
  'quote_request.delete':  { label: 'Deleted Quote Request', color: 'bg-red-50 text-red-700 border-red-200' },
};

const ROLE_BADGE = {
  master_admin: 'bg-gradient-to-r from-amber-100 to-orange-100 text-orange-700 border-orange-300',
  admin: 'bg-purple-50 text-purple-700 border-purple-200',
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' }); }
  catch { return iso; }
};

const AuditRow = ({ entry, expanded, onToggle }) => {
  const cfg = ACTION_LABELS[entry.action] || { label: entry.action, color: 'bg-zinc-100 text-zinc-700 border-zinc-200' };
  const roleColor = ROLE_BADGE[entry.actor_role] || 'bg-zinc-100 text-zinc-600';
  const hasDetails = entry.details && Object.keys(entry.details).length > 0;

  return (
    <>
      <motion.tr
        initial={{ opacity: 0, y: 4 }}
        animate={{ opacity: 1, y: 0 }}
        className="border-b border-zinc-100 hover:bg-zinc-50 cursor-pointer"
        onClick={() => hasDetails && onToggle(entry.id)}
        data-testid={`row-${entry.id}`}
      >
        <td className="py-3 px-4">
          <Badge variant="outline" className={`text-[10px] ${cfg.color}`}>{cfg.label}</Badge>
        </td>
        <td className="py-3 px-4">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-full bg-gradient-to-br from-[#F56F05] to-[#C2410C] flex items-center justify-center text-white text-[10px] font-bold uppercase">
              {(entry.actor_email || '?').slice(0, 2)}
            </div>
            <div>
              <p className="text-sm font-semibold text-zinc-900">{entry.actor_email || '—'}</p>
              <Badge variant="outline" className={`text-[9px] ${roleColor}`}>
                {entry.actor_role === 'master_admin' ? 'Master Admin' : entry.actor_role}
              </Badge>
            </div>
          </div>
        </td>
        <td className="py-3 px-4 text-xs text-zinc-500">{entry.target_type || '—'}</td>
        <td className="py-3 px-4 text-xs text-zinc-500 font-mono">{entry.target_id ? entry.target_id.slice(0, 8) : '—'}</td>
        <td className="py-3 px-4 text-xs text-zinc-500">{fmtDate(entry.created_at)}</td>
        <td className="py-3 px-4 text-right">
          {hasDetails && (
            <ChevronDown className={`w-4 h-4 text-zinc-400 inline-block transition-transform ${expanded ? 'rotate-180' : ''}`} />
          )}
        </td>
      </motion.tr>
      {expanded && hasDetails && (
        <tr className="bg-zinc-50/50">
          <td colSpan={6} className="px-4 py-3">
            <pre className="text-[11px] text-zinc-600 font-mono whitespace-pre-wrap bg-white border border-zinc-200 rounded-lg p-3 max-h-48 overflow-auto">
              {JSON.stringify(entry.details, null, 2)}
            </pre>
          </td>
        </tr>
      )}
    </>
  );
};

const StatCard = ({ label, value, sub }) => (
  <div className="bg-white rounded-2xl border border-zinc-200 p-4 min-w-0">
    <p className="text-xs uppercase tracking-wider font-semibold text-zinc-500 break-words">{label}</p>
    <p className="text-2xl font-extrabold text-zinc-900 mt-1">{value}</p>
    {sub && <p className="text-xs text-zinc-400 mt-1 break-words">{sub}</p>}
  </div>
);

const AdminAuditLogPage = () => {
  const { token } = useAuth();
  const [entries, setEntries] = useState([]);
  const [stats, setStats] = useState({ total: 0, by_action: [] });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [actionFilter, setActionFilter] = useState('all');
  const [expanded, setExpanded] = useState(null);

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.actor_email = search;
      if (actionFilter !== 'all') params.action = actionFilter;
      const [listRes, statsRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/audit-log`, { headers, params }),
        axios.get(`${API_URL}/api/admin/audit-log/stats`, { headers }),
      ]);
      setEntries(listRes.data);
      setStats(statsRes.data);
    } catch {
      toast.error('Failed to load audit log');
    } finally {
      setLoading(false);
    }
  }, [headers, search, actionFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="admin-audit-log-page">
      <header className="bg-white border-b border-zinc-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between min-h-16 py-3 gap-3 flex-wrap">
            <div className="flex items-center gap-3 min-w-0 flex-1">
              <Link to="/admin/dashboard" className="text-zinc-500 hover:text-zinc-900 flex-shrink-0" data-testid="back-to-dashboard">
                <ArrowLeft className="w-5 h-5" />
              </Link>
              <div className="w-10 h-10 rounded-xl bg-[#F56F05]/10 flex items-center justify-center flex-shrink-0">
                <FileText className="w-5 h-5 text-[#F56F05]" />
              </div>
              <div className="min-w-0">
                <h1 className="text-lg font-bold text-zinc-900">Audit Log</h1>
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={fetchData} data-testid="refresh-btn">
              <RefreshCw className="w-4 h-4 sm:mr-2" /> <span className="hidden sm:inline">Refresh</span>
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <StatCard label="Total Events" value={stats.total} />
          {(stats.by_action || []).slice(0, 3).map(s => (
            <StatCard key={s.action} label={ACTION_LABELS[s.action]?.label || s.action} value={s.count} />
          ))}
        </div>

        {/* Filters */}
        <Card className="border-zinc-200 mb-4">
          <CardContent className="p-4 flex flex-col md:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
              <Input
                placeholder="Search by admin email..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
                data-testid="audit-search"
              />
            </div>
            <Select value={actionFilter} onValueChange={setActionFilter}>
              <SelectTrigger className="w-full md:w-64" data-testid="action-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Actions</SelectItem>
                {Object.keys(ACTION_LABELS).map(k => (
                  <SelectItem key={k} value={k}>{ACTION_LABELS[k].label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </CardContent>
        </Card>

        {/* Table */}
        <Card className="border-zinc-200 overflow-hidden min-w-0">
          {loading ? (
            <div className="p-12 flex items-center justify-center">
              <Loader2 className="w-6 h-6 animate-spin text-[#F56F05]" />
            </div>
          ) : entries.length === 0 ? (
            <div className="p-12 text-center" data-testid="empty-state">
              <FileText className="w-12 h-12 text-zinc-300 mx-auto mb-3" />
              <p className="text-zinc-600 font-semibold">No audit entries yet</p>
              <p className="text-sm text-zinc-400 mt-1">Admin actions will be recorded here as they happen.</p>
            </div>
          ) : (
            <div className="overflow-x-auto min-w-0 max-w-full">
              <table className="w-full" data-testid="audit-table">
                <thead className="bg-zinc-50 border-b border-zinc-200">
                  <tr>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-zinc-500 uppercase tracking-wider">Action</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-zinc-500 uppercase tracking-wider">Admin</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-zinc-500 uppercase tracking-wider">Target</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-zinc-500 uppercase tracking-wider">ID</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-zinc-500 uppercase tracking-wider">When</th>
                    <th className="w-10" />
                  </tr>
                </thead>
                <tbody>
                  {entries.map(e => (
                    <AuditRow key={e.id} entry={e} expanded={expanded === e.id} onToggle={(id) => setExpanded(expanded === id ? null : id)} />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </main>
    </div>
  );
};

export default AdminAuditLogPage;
